import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { StationV2 } from '../../../../types/dashboardV2';
import { formatStatus, getStatusBadgeClass } from '../../../../shared/utils/statusUtils';
import {
  DataTable,
  type DataTableColumnMeta,
  type DataTableRenderers,
} from '../../components/DataTable';
import { StatusBadgeCell } from '../../components/cells';

interface OrganizationStationsTableProps {
  stations: StationV2[];
  onDetach: (station: StationV2) => void;
}

/** Column shares below are shares of this; narrower viewports scroll the table instead. */
export const ORGANIZATION_STATIONS_TABLE_MIN_WIDTH = '800px';

export const ORGANIZATION_STATIONS_COLUMNS = [
  { key: 'idTag', labelKey: 'dashboard.tables.stations.idTag', width: '23.5%', cellClassName: 'cell-id', skeletonVariant: 'id' },
  { key: 'address', labelKey: 'dashboard.tables.stations.address', width: '35.5%', skeletonVariant: 'address' },
  { key: 'status', labelKey: 'dashboard.tables.stations.status', width: '22.5%', skeletonVariant: 'status' },
  { key: 'actions', labelKey: 'dashboard.tables.actions', width: '18.5%', align: 'right', skeletonVariant: 'action' },
] as const satisfies readonly DataTableColumnMeta[];

export const OrganizationStationsTable = memo(function OrganizationStationsTable({
  stations,
  onDetach,
}: OrganizationStationsTableProps) {
  const { t } = useTranslation();

  const renderers = useMemo<
    DataTableRenderers<StationV2, typeof ORGANIZATION_STATIONS_COLUMNS>
  >(
    () => ({
      idTag: (s) => s.idTag,
      address: (s) => s.address,
      status: (s) => (
        <StatusBadgeCell badgeClass={getStatusBadgeClass(s.status)} label={formatStatus(s.status)} />
      ),
      actions: (s) => (
        <button
          type="button"
          className="secondary-action-btn"
          onClick={() => onDetach(s)}
          aria-label={t('dashboard.organizationStations.detachAria', { idTag: s.idTag })}
        >
          {t('dashboard.organizationStations.detach')}
        </button>
      ),
    }),
    [t, onDetach],
  );

  return (
    <DataTable
      columns={ORGANIZATION_STATIONS_COLUMNS}
      renderers={renderers}
      rows={stations}
      rowKey={(s) => s.id}
      minWidth={ORGANIZATION_STATIONS_TABLE_MIN_WIDTH}
    />
  );
});

export default OrganizationStationsTable;
